import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { UploadCloud } from "lucide-react";
import { Demos, type Demo } from "@/api/demos";
import { Accounts } from "@/api/endpoints";
import TopBar from "@/components/TopBar";
import { Empty, Segmented } from "@/components/ui";
import DemoGrid from "@/components/demos/DemoGrid";
import { UploadDialog } from "@/components/demos/DemoDialogs";

type KindFilter = "all" | "video" | "audio" | "docs";

const matchesKind = (d: Demo, k: KindFilter) =>
  k === "all" || (k === "docs" ? !["video", "audio"].includes(d.kind) : d.kind === k);

/** Product demos: upload anything, play video/audio in place, link to accounts. */
export default function DemosPage() {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<KindFilter>("all");
  const [accountId, setAccountId] = useState("");
  const [uploading, setUploading] = useState(false);

  const demosQuery = useQuery({
    queryKey: ["demos", { q, accountId }],
    queryFn: () => Demos.list({ q: q || undefined, account_id: accountId || undefined }),
  });
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => Accounts.list() });
  const demos = (demosQuery.data ?? []).filter((d) => matchesKind(d, kind));

  return (
    <>
      <TopBar
        title="Demos"
        subtitle="Product demos to show prospects. Video and audio play right here."
        onSearch={setQ}
        searchPlaceholder="Search demos…"
        actions={
          <button className="btn btn-primary" onClick={() => setUploading(true)}>
            <UploadCloud className="h-3.5 w-3.5" /> Upload
          </button>
        }
      />
      <div className="space-y-5 px-4 py-4 md:px-6 md:py-6">
        <div className="flex flex-wrap items-center gap-3">
          <Segmented<KindFilter>
            label="Type"
            value={kind}
            onChange={setKind}
            options={[
              { value: "all", label: "All" },
              { value: "video", label: "Video" },
              { value: "audio", label: "Audio" },
              { value: "docs", label: "Docs & files" },
            ]}
          />
          <select
            className="input !w-auto !py-1.5 text-sm"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            aria-label="Filter by account"
          >
            <option value="">All accounts</option>
            {(accountsQuery.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        {demosQuery.isLoading ? (
          <p className="text-md text-mut-500">Loading…</p>
        ) : demos.length === 0 ? (
          <div className="panel">
            <Empty title={q || accountId || kind !== "all" ? "No demos match" : "No demos yet"}>
              Upload product videos, recordings, decks or one-pagers, and link them to the accounts
              they suit.
            </Empty>
            <div className="-mt-4 pb-8 text-center">
              <button className="btn btn-primary" onClick={() => setUploading(true)}>
                <UploadCloud className="h-3.5 w-3.5" /> Upload a demo
              </button>
            </div>
          </div>
        ) : (
          <DemoGrid demos={demos} />
        )}
      </div>
      {uploading && <UploadDialog onClose={() => setUploading(false)} />}
    </>
  );
}
